import { rememberUIComponentModule } from '../remember-ui/remember-ui';
import { scopeHelperModule } from '../../helpers/scope-helper';
import tabTemplate from './tab.tpl.html';
import tabSetTemplate from './tabset.tpl.html';
import './tabset.less';
import {
  WINDOW_EVT_RESIZE,
  windowEventHelperModule,
} from '../../helpers/window-event-helper/window-event-helper';
import { mousewheelComponentModule } from '../../vendor/mousewheel-component-module';
import { stopEvent } from '../../utils/event-utils';
import { keycodesModule } from '../../constants/keycodes';
import { UI_LAYOUT_EVT_RESIZE } from '../ui-layout/ui-layout';

const SCROLLER_BUTTON_WIDTH = 25;

export const TABSET_EVT_SELECT = 'tabset.select';

/*
 * Heavily modified replacement for ui-bootstrap tabs
 */

export const tabsComponentModule = angular
  .module('Q.components.tabs', [
    rememberUIComponentModule,
    mousewheelComponentModule,
    scopeHelperModule,
    windowEventHelperModule,
    keycodesModule,
  ])

  .controller(
    'TabsetCtrl',
    function TabsetCtrl($scope, $element, $timeout, scopeHelper) {
      const ctrl = this;
      ctrl.$onInit = () => {
        const tabs = (ctrl.tabs = []);
        const outerScroller = $element[0].querySelector('.tab-container');
        const innerScroller = $element[0].querySelector('.tab-container > ul');
        let scrollOffset = 0;
        let destroyed;

        if (ctrl.scrollable === undefined) ctrl.scrollable = true;

        ctrl.select = function (selectedTab) {
          angular.forEach(tabs, function (tab) {
            if (tab.active && tab !== selectedTab) {
              tab.active = false;
              tab.onDeselect();
            }
          });
          selectedTab.active = true;
          selectedTab.onSelect();
          $timeout(() => {
            ctrl.scrollToTab(selectedTab);
            $scope.$broadcast(TABSET_EVT_SELECT, selectedTab);
          }, 10);
        };

        ctrl.addTab = function addTab(tab) {
          tabs.push(tab);
          // we can't run the select function on the first tab
          // since that would select it twice
          if (tabs.length === 1) {
            tab.active = true;
          } else if (tab.active || tab.defaultTab) {
            ctrl.select(tab);
          }
        };

        ctrl.removeTab = function removeTab(tab) {
          const index = tabs.indexOf(tab);
          //Select a new tab if the tab to be removed is selected and not destroyed
          if (tabs.length > 1 && !destroyed) {
            if (tab.active) {
              // If this is the last tab, select the previous tab. else, the next tab.
              const newActiveIndex =
                index === tabs.length - 1 ? index - 1 : index + 1;
              ctrl.select(tabs[newActiveIndex]);
            } else {
              // just refresh the layout
              _resizeDebounce();
            }
          }
          tabs.splice(index, 1);
        };

        //// SCROLLING

        let maxScroll = 0;

        function _setMaxScroll() {
          maxScroll = innerScroller.offsetWidth - outerScroller.offsetWidth;
        }

        // this method is designed to only update once at the beginning of each digest cycle, to
        // prevent infinite digest errors
        scopeHelper.oncePerDigest(_setMaxScroll, $scope);

        function _isVisible(el) {
          return !angular.element(el).isHidden();
        }

        ctrl.scrollToTab = function (tab) {
          _setMaxScroll();
          const el =
            tab.element ||
            (angular.isNumber(tab) ? innerScroller.children[tab] : tab);
          const elLeft = el.offsetLeft;
          const elRight = elLeft + el.offsetWidth;

          // ensure right is on-screen
          let scrollRight = scrollOffset + outerScroller.offsetWidth;
          if (scrollOffset < maxScroll) {
            scrollRight -= SCROLLER_BUTTON_WIDTH;
          }
          if (elRight > scrollRight) {
            scrollOffset += elRight - scrollRight;
          }

          // ensure left is on-screen
          let scrollLeft = scrollOffset;
          if (scrollOffset > 0) {
            scrollLeft += SCROLLER_BUTTON_WIDTH;
          }
          if (elLeft < scrollLeft) {
            scrollOffset = elLeft - SCROLLER_BUTTON_WIDTH;
          }

          // catch scrolling too far on resize events
          scrollOffset = Math.max(0, Math.min(maxScroll, scrollOffset));
        };
        ctrl.shouldScroll = function () {
          if (ctrl.scrollable) {
            return maxScroll > 0;
          } else {
            return false;
          }
        };

        ctrl.getScrollOffset = function () {
          return -scrollOffset + 'px';
        };
        ctrl.hasLeft = function () {
          return scrollOffset > 0;
        };
        ctrl.scrollLeft = function ($event) {
          if (ctrl.hasLeft()) {
            $event.preventDefault();
            for (let i = innerScroller.children.length - 1; i >= 0; i--) {
              const child = innerScroller.children[i];
              if (
                _isVisible(child) &&
                innerScroller.children[i].offsetLeft < scrollOffset
              ) {
                ctrl.scrollToTab(child);
                break;
              }
            }
          }
        };
        ctrl.hasRight = function () {
          return scrollOffset < maxScroll;
        };
        ctrl.scrollRight = function ($event) {
          if (ctrl.hasRight()) {
            $event.preventDefault();
            const scrollRight = scrollOffset + outerScroller.offsetWidth;
            for (let i = 0; i < innerScroller.children.length; i++) {
              const child = innerScroller.children[i];
              if (
                _isVisible(child) &&
                child.offsetLeft + child.offsetWidth > scrollRight
              ) {
                ctrl.scrollToTab(child);
                break;
              }
            }
          }
        };
        ctrl.scrollWheel = function ($event, $delta) {
          if ($delta && ctrl.shouldScroll()) {
            $event.preventDefault();
            scrollOffset = Math.max(
              0,
              Math.min(maxScroll, scrollOffset - 20 * $delta),
            );
          }
        };

        let _resizePromise;

        function _resize() {
          _setMaxScroll();
          for (let i = 0; i < tabs.length; i++) {
            if (tabs[i].active) {
              ctrl.scrollToTab(tabs[i]);
              break;
            }
          }
        }

        function _resizeDebounce() {
          if (_resizePromise) return;
          // delay next resize for at least 250ms
          _resizePromise = $timeout(() => {
            _resizePromise = null;
            _resize();
          }, 250);
        }

        $scope.$on(WINDOW_EVT_RESIZE, _resizeDebounce);
        $scope.$on(UI_LAYOUT_EVT_RESIZE, _resizeDebounce);

        $scope.$on('$destroy', function () {
          destroyed = true;
        });
      };
    },
  )

  .directive('tabset', function ($timeout, $window) {
    return {
      require: ['tabset', '?rememberUi'],
      restrict: 'EA',
      transclude: true,
      replace: true,
      scope: {
        type: '@',
        scrollable: '=?',
      },
      controller: 'TabsetCtrl as tabsetCtrl',
      bindToController: true,
      template: tabSetTemplate,
      link: function (scope, element, attrs, [tabsetCtrl, rememberUi]) {
        tabsetCtrl.vertical = angular.isDefined(attrs.vertical)
          ? scope.$parent.$eval(attrs.vertical)
          : false;
        tabsetCtrl.justified = angular.isDefined(attrs.justified)
          ? scope.$parent.$eval(attrs.justified)
          : false;

        if (rememberUi) {
          const defaultTab = rememberUi.retrieve();
          // need to delay one cycle for ng-repeated tabs
          $timeout(function () {
            if (defaultTab && tabsetCtrl.tabs[defaultTab]) {
              tabsetCtrl.select(tabsetCtrl.tabs[defaultTab]);
            }
          });
          scope.$on(TABSET_EVT_SELECT, function () {
            let which = -1;
            angular.forEach(tabsetCtrl.tabs, function (tab, i) {
              if (tab.active) {
                which = i;
              }
            });
            if (which > -1) {
              rememberUi.store(which);
            } else {
              rememberUi.forget();
            }
          });
        }

        // enable animations after the initial digest
        $window.requestAnimationFrame(
          () =>
            (element[0].querySelector('.tab-scroller').className += ' init'),
        );
      },
    };
  })

  .controller('TabCtrl', function TabCtrl($element) {
    const tabCtrl = this;

    tabCtrl.disabled = false;
    tabCtrl.active = false;
    tabCtrl.element = $element[0];

    tabCtrl.select = function ($event) {
      stopEvent($event);
      if (!tabCtrl.disabled) {
        tabCtrl.onSelect();
        tabCtrl.active = true;
      }
    };
  })
  .directive('tab', function ($parse, KeyCodes) {
    return {
      require: ['tab', '^tabset'],
      restrict: 'EA',
      replace: true,
      template: tabTemplate,
      transclude: true,
      scope: {
        defaultTab: '=?',
        active: '=?',
        heading: '@',
        onSelect: '&',
        onDeselect: '&',
        noHighlight: '=',
      },
      controller: 'TabCtrl as tabCtrl',
      bindToController: true,
      link: function postLink(
        scope,
        elm,
        attrs,
        [tabCtrl, tabsetCtrl],
        transclude,
      ) {
        tabCtrl.classes = elm.attr('class');

        scope.$watch('tabCtrl.active', function (active) {
          if (active) {
            tabsetCtrl.select(tabCtrl);
          }
        });

        elm.querySelector('li > a').on('keydown', (event) => {
          if (
            event.which === KeyCodes.ENTER ||
            event.which === KeyCodes.SPACE
          ) {
            tabCtrl.select(event);
          }
        });

        tabCtrl.disabled = false;
        if (attrs.disabled) {
          scope.$parent.$watch($parse(attrs.disabled), function (value) {
            tabCtrl.disabled = !!value;
          });
        }

        tabsetCtrl.addTab(tabCtrl);
        scope.$on('$destroy', function () {
          tabsetCtrl.removeTab(tabCtrl);
        });

        //We need to transclude later, once the content container is ready.
        //when this link happens, we're inside a tab heading.
        tabCtrl.$transclude = function (fn) {
          transclude(scope.$parent, fn);
        };
      },
    };
  })

  .directive('tabHeadingTransclude', function () {
    return {
      restrict: 'A',
      require: '^tab',
      link: function (scope, elm) {
        scope.$watch(
          'tabCtrl.headingElement',
          function updateHeadingElement(heading) {
            if (heading) {
              elm.html('');
              elm.append(heading);
            }
          },
        );
      },
    };
  })

  .directive('tabContentTransclude', function () {
    return {
      restrict: 'A',
      require: '^tabset',
      link: function (scope, elm, attrs) {
        const tab = scope.$eval(attrs.tabContentTransclude);
        scope.tabCtrl = tab;

        // Now our tab is ready to be transcluded: both the tab heading area
        // and the tab content area are loaded.  Transclude 'em both.
        tab.$transclude(function (contents) {
          angular.forEach(contents, function (node) {
            if (isTabHeading(node)) {
              //Let tabHeadingTransclude know.
              tab.headingElement = node;
            } else {
              elm.append(node);
            }
          });
        });
      },
    };

    function isTabHeading(node) {
      return (
        node.tagName &&
        (node.hasAttribute('tab-heading') ||
          node.hasAttribute('data-tab-heading') ||
          node.tagName.toLowerCase() === 'tab-heading' ||
          node.tagName.toLowerCase() === 'data-tab-heading')
      );
    }
  });
